<script setup lang="ts">
import type { SerializedEditorState } from "lexical";
import type { Moment } from "moment";
import moment from "moment";
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";

import actions from "~/actions";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { createNewTaskNotification } from "~/components/notifications/notification";
import TitleEditor from "~/components/text/TitleEditor.vue";
import { convertToTask } from "~/components/text/utils";
import { InfoIcon } from "~/icons";
import { CommandId, EditorMode, SubtaskDisplayMode } from "~/shared/enums";
import { useAppStore, useDataStore, usePageStore } from "~/stores";
import { calculateListLeftPadding, makeEmptyLexicalState } from "~/utils/common";

const NLP_DESCRIPTION =
  "Use natural language to quickly fill out task properties. For example, type a teammate's name to assign them. To learn more, look in Help and resources > Shortcuts (or type ?) and look for Quick add.";

const router = useRouter();
const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();

const titleEditor = ref<InstanceType<typeof TitleEditor> | null>(null);

const title = ref<SerializedEditorState>(appStore.cachedNewTaskInput ?? makeEmptyLexicalState());
const isTitleEmpty = computed(() => !convertToTask(title.value).title);
const notFlat = computed(() => appStore.subtaskDisplayMode !== SubtaskDisplayMode.FLAT);

const leftMargin = computed(
  () => calculateListLeftPadding(notFlat.value, pageStore.isMobile, pageStore.hasTouch) + (pageStore.isMobile ? -5 : 0)
);

const focus = () => titleEditor.value?.focus();

const onChange = (newTitle: SerializedEditorState) => {
  title.value = newTitle;
  appStore.cachedNewTaskInput = newTitle;
};

const onPasteMultilineText = async (text: string) => {
  const currentDartboard = appStore.currentDartboardOrDefault;
  if (!currentDartboard) {
    return;
  }

  const partialTask = convertToTask(title.value);
  actions.task.createTasksFromText(text, currentDartboard.duid, "", currentDartboard.topTaskOrder, partialTask, {
    createAll: true,
  });
};

const onSave = async () => {
  const partialTask = convertToTask(title.value);
  if (!partialTask.title) {
    focus();
    return;
  }

  const savedTask = await actions.visualization.addTask(partialTask.title, partialTask);
  focus();
  nextTick(() => {
    nextTick(() => {
      titleEditor.value?.clear();
      focus();
    });
  });

  if (!savedTask || savedTask.dartboardDuid === appStore.currentPage?.duid) {
    return;
  }

  const dartboard = dataStore.getDartboardByDuid(savedTask.dartboardDuid);
  if (!dartboard) {
    return;
  }

  createNewTaskNotification(
    [savedTask],
    dartboard,
    dataStore.getSpaceByDuid,
    actions.visualization.navigateToTask,
    router
  );
};

// TODO remove this hack when the grid doesn't randomly unmount the headers
let lastBlur: Moment | undefined;
const onBlur = () => {
  lastBlur = moment();
};

onMounted(() => {
  appStore.newTask = {
    focus,
  };
});

onUnmounted(() => {
  if (appStore.newTask?.focus === focus) {
    appStore.newTask = null;
  }
  if (lastBlur && lastBlur.isAfter(moment().subtract(100, "millisecond"))) {
    appStore.newTask?.focus();
  }
});
</script>

<template>
  <div
    id="new-task"
    class="relative flex size-full cursor-text items-center overflow-hidden pr-7 focus-within:bg-lt hover:bg-lt"
    :style="{ marginLeft: `${leftMargin}px` }"
    @click.stop="focus"
    @keydown.enter.stop="focus"
    @keydown.arrow-up.stop
    @keydown.arrow-down.stop
    @keydown.arrow-left.stop
    @keydown.arrow-right.stop>
    <TitleEditor
      ref="titleEditor"
      :initial-title="title"
      :editor-mode="EditorMode.LIST"
      @change="onChange"
      @blur="onBlur"
      @enter="onSave"
      @paste-multiline-text="onPasteMultilineText" />
    <Tooltip v-if="!isTitleEmpty" :command-id="CommandId.SAVE_TASK" class="ml-1.5">
      <button
        type="button"
        aria-label="Save task"
        class="flex icon-md flex-none items-center justify-center rounded-sm border border-oncolor text-sm focus-ring-none hover:bg-opposite/10 dark:hover:bg-white/10"
        @click.stop="onSave"
        @keydown.enter="onSave">
        <span class="icon-sm text-sm text-primary-base select-none">↵</span>
      </button>
    </Tooltip>
    <div class="flex-1" @click.stop="focus()" @keydown.enter.stop="focus()" />
    <Tooltip v-if="!pageStore.isMobile" :text="NLP_DESCRIPTION" info class="absolute right-0 mr-2">
      <InfoIcon class="icon-xs cursor-help rounded-full text-vlt" />
    </Tooltip>
  </div>
</template>
